import React, { useState } from "react";
import { ProductGalleryItem } from "../../../../../../services/helpers/ProductImageHelper";
import mediaUtility from "../../../../../../services/utility/MediaUtility";
import "./styles/MobileGallery.css";

interface MobileGalleryProps {
  /** Images + videos in payload order (backend order = display order). */
  items: ProductGalleryItem[];
  currentIndex: number;
  onIndexChange: (idx: number) => void;
  onOpenViewer: (idx: number) => void;
  imageError: boolean;
  headerOverlay?: React.ReactNode;
  headerFixed?: React.ReactNode;
}

const MIN_SWIPE_PX = 50;

const MobileGallery: React.FC<MobileGalleryProps> = ({
  items,
  currentIndex,
  onIndexChange,
  onOpenViewer,
  imageError,
  headerOverlay,
  headerFixed,
}) => {
  const [touchStart, setTouchStart] = useState<number | null>(null);
  const [touchEnd, setTouchEnd] = useState<number | null>(null);
  const [isSwiping, setIsSwiping] = useState(false);
  const [internalError, setInternalError] = useState(false);

  const handleTouchStart = (e: React.TouchEvent) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
    setIsSwiping(false);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    const x = e.targetTouches[0].clientX;
    setTouchEnd(x);
    if (touchStart !== null && Math.abs(x - touchStart) > 10) setIsSwiping(true);
  };

  const handleTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    const delta = touchStart - touchEnd;
    if (delta > MIN_SWIPE_PX && currentIndex < items.length - 1)
      onIndexChange(currentIndex + 1);
    else if (delta < -MIN_SWIPE_PX && currentIndex > 0)
      onIndexChange(currentIndex - 1);
    setTimeout(() => setIsSwiping(false), 100);
  };

  const hasError = imageError || internalError;
  const current = items[currentIndex] || items[0];

  return (
    <div className="mgal-root">
      <div
        className="mgal-stage"
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        onClick={() => {
          if (!isSwiping && !hasError && items.length > 0) onOpenViewer(currentIndex);
        }}
      >
        {items.length === 0 && !hasError ? (
          <div className="mgal-placeholder">
            <div className="mgal-spinner" />
            <p>Loading media...</p>
          </div>
        ) : hasError || !current ? (
          <div className="mgal-bg mgal-bg--error" />
        ) : current.kind === "video" ? (
          <>
            {/* Inline preview: muted looping autoplay (Instagram-style).
                Sound + controls live in the fullscreen viewer (tap). */}
            <video
              key={current.url}
              className="mgal-video"
              src={current.url}
              muted
              loop
              autoPlay
              playsInline
              preload="metadata"
            />
            <div className="mgal-overlay" />
          </>
        ) : (
          <>
            <div
              className="mgal-bg"
              style={{ backgroundImage: mediaUtility.cssUrl(current.url) }}
            >
              <img
                src={current.url}
                alt="product"
                style={{ display: "none" }}
                onError={() => setInternalError(true)}
              />
            </div>
            <div className="mgal-overlay" />
          </>
        )}

        {headerOverlay && (
          <div className="mgal-header-overlay">{headerOverlay}</div>
        )}
        {headerFixed && (
          <div className="mgal-header-fixed">{headerFixed}</div>
        )}

        {items.length > 1 && (
          <div className="mgal-counter">
            <span className="mgal-counter__current">{currentIndex + 1}</span>
            <span className="mgal-counter__sep">/</span>
            <span className="mgal-counter__total">{items.length}</span>
            <div className="mgal-progress">
              <div
                className="mgal-progress__fill"
                style={{ width: `${((currentIndex + 1) / items.length) * 100}%` }}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default MobileGallery;
